import React from 'react';
import {view, Icon} from '@sail/ui';
import {Select, Text} from 'src/v2/components/common';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {COLORS} from 'src/v2/util/color';
import type {CurrencyBalance} from 'src/v2/state/useCurrencyBalances';
import {formatAmountToDollars} from 'src/utils/helpers';

interface CurrencySelectorProps {
  selectedCurrencyId: string;
  onCurrencySelect: (currencyId: string) => void;
  label: string;
  currencyBalances: CurrencyBalance[];
  disabled?: boolean;
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  selectedCurrencyId,
  onCurrencySelect,
  label,
  currencyBalances,
  disabled = false,
}) => {
  const items = currencyBalances.map(({currency, amountInUsdMinor}) => ({
    id: currency.id,
    label: currency.assetCode.toUpperCase(),
    description:
      amountInUsdMinor !== null ? (
        <Text type="input">{formatAmountToDollars(amountInUsdMinor)}</Text>
      ) : undefined,
    icon: getCurrencyIcon(currency.assetCode),
  }));

  const isSingleCurrency = items.length === 1;

  if (isSingleCurrency) {
    const currency = items[0];

    return (
      <view.div
        css={{
          backgroundColor: COLORS.offsetOnLight,
          border: 'none',
          borderRadius: 'small',
          padding: 'space.150',
          stack: 'x',
          alignItems: 'center',
          distribute: 'space-between',
          width: 'fill',
          height: '44px',
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gapX: 'small',
            alignItems: 'center',
          }}
        >
          <Icon icon={currency.icon} css={{borderRadius: 'xsmall'}} />
          <Text type="bodyEmphasized">{currency.label}</Text>
        </view.div>
        {/* token balance */}
        {currency.description ? (
          <Text type="input">{currency.description}</Text>
        ) : null}
      </view.div>
    );
  }

  // Multiple currencies - render existing Select component
  return (
    <Select
      // The component doesn't rerender when balances update. Force a rerender by using a key.
      key={`balance-${disabled}-${items?.length ?? 0}`}
      items={items}
      selectedItemId={selectedCurrencyId}
      onItemSelect={onCurrencySelect}
      label={label}
      disabled={disabled}
    />
  );
};
